import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Home as HomeIcon, Heart as HeartIcon, ShoppingBag as ShoppingBagIcon, Truck as TruckIcon, ArrowLeft, Loader } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import './Category.css';



const categoryMeta = {
  'almonds': { name: 'Almonds', hindi: 'Badam' },
  'cashews': { name: 'Cashews', hindi: 'Kaju' },
  'walnuts': { name: 'Walnuts', hindi: 'Akhrot' },
  'pistachios': { name: 'Pistachios', hindi: 'Pista' },
  'makhana': { name: 'Makhana', hindi: 'Makhana' },
  'raisins': { name: 'Raisins', hindi: 'Kishmish' },
  'seeds & others': { name: 'Seeds & Others', hindi: 'Beej' },
  'combo packs': { name: 'Combo Packs', hindi: 'Combo' }
};

const filterProductsByCategory = (products, categoryName) => {
  const normCategory = categoryName.toLowerCase();
  
  if (normCategory === 'almonds') {
    return products.filter(p => p.type.toLowerCase() === 'almonds' || p.type.toLowerCase() === 'badam' || (p.type.toLowerCase() === 'nuts' && p.title.toLowerCase().includes('almond')));
  }
  if (normCategory === 'cashews') {
    return products.filter(p => p.type.toLowerCase() === 'cashews' || p.type.toLowerCase() === 'kaju' || (p.type.toLowerCase() === 'nuts' && p.title.toLowerCase().includes('cashew')));
  }
  if (normCategory === 'walnuts') {
    return products.filter(p => p.type.toLowerCase() === 'walnuts' || p.type.toLowerCase() === 'akhrot' || (p.type.toLowerCase() === 'nuts' && p.title.toLowerCase().includes('walnut')));
  }
  if (normCategory === 'pistachios') {
    return products.filter(p => p.type.toLowerCase() === 'pistachios' || p.type.toLowerCase() === 'pista' || (p.type.toLowerCase() === 'nuts' && p.title.toLowerCase().includes('pistachio')));
  }
  if (normCategory === 'makhana') {
    return products.filter(p => p.type.toLowerCase() === 'makhana' || p.title.toLowerCase().includes('makhana'));
  }
  if (normCategory === 'raisins') {
    return products.filter(p => p.type.toLowerCase() === 'raisins' || p.type.toLowerCase() === 'kishmish' || p.title.toLowerCase().includes('raisin'));
  }
  if (normCategory === 'seeds & others' || normCategory === 'seeds' || normCategory === 'seeds-others') {
    return products.filter(p => p.type.toLowerCase() === 'seeds' || p.type.toLowerCase() === 'beej' || p.type.toLowerCase().includes('seed') || p.title.toLowerCase().includes('seed') || p.title.toLowerCase().includes('chia') || p.title.toLowerCase().includes('pumpkin') || p.title.toLowerCase().includes('sunflower'));
  }
  if (normCategory === 'combo packs' || normCategory === 'combo' || normCategory === 'combo-packs') {
    return products.filter(p => p.type.toLowerCase() === 'combo' || p.type.toLowerCase().includes('combo') || p.title.toLowerCase().includes('combo') || p.title.toLowerCase().includes('pack'));
  }
  
  // Fallback: match by type directly or by title matching category name
  return products.filter(p => p.type.toLowerCase() === normCategory || p.title.toLowerCase().includes(normCategory));
};

const Category = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const { wishlistCount, cartCount } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryName]);

  useEffect(() => {
    setLoading(true);
    setError('');
    fetch(`${API_URL}/products`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load products');
        return res.json();
      })
      .then((data) => {
        if (Array.isArray(data)) {
          const filtered = filterProductsByCategory(data, categoryName);
          setProducts(filtered);
        } else {
          throw new Error('Invalid products format');
        }
      })
      .catch((err) => {
        console.error('Error fetching products:', err);
        setError('Error loading products. Please try again later.');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [categoryName]);

  const meta = categoryMeta[categoryName.toLowerCase()] || { name: categoryName, hindi: null };

  return (
    <div className="category-page-wrapper">
      {/* Top announcement bar */}
      <TopBar />

      {/* Main Header */}
      <Header 
        wishlistCount={wishlistCount} 
        cartCount={cartCount} 
        onCartClick={() => navigate('/cart')} 
      />

      {/* Navigation Bar */}
      <Navbar activeItem="Shop by Category" />

      {/* Main content */}
      <main className="category-main">
        <div className="container">
          <div className="category-header-row">
            <Link to="/" className="back-btn">
              <ArrowLeft size={16} />
              <span>Back to Shop</span>
            </Link>
            <h1 className="category-page-title">
              {meta.name}
              {meta.hindi && <span className="hindi-title-span"> ({meta.hindi})</span>}
            </h1>
          </div>

          {loading ? (
            <div className="category-loader-container">
              <Loader className="spinner" size={40} />
              <p>Loading premium products...</p>
            </div>
          ) : error ? (
            <div className="category-error-state">
              <p>{error}</p>
            </div>
          ) : products.length > 0 ? (
            <div className="category-products-grid">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="category-empty-state">
              <h2>No Products Found</h2>
              <p>We are currently stocking up on new arrivals for this category. Check back soon!</p>
              <Link to="/" className="btn-return-home">
                Go to Homepage
              </Link>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <Footer />

      {/* Sticky Bottom Navigation (Mobile screens only) */}
      <div className="mobile-sticky-nav" role="navigation" aria-label="Mobile Navigation">
        <Link 
          to="/" 
          className="mobile-nav-item"
        >
          <HomeIcon size={20} />
          <span>Home</span>
        </Link>
        <Link 
          to="/track" 
          className="mobile-nav-item"
        >
          <TruckIcon size={20} />
          <span>Track Order</span>
        </Link>
        <Link 
          to="/wishlist" 
          className="mobile-nav-item"
        >
          <HeartIcon size={20} />
          {wishlistCount > 0 && (
            <span className="badge-count">{wishlistCount}</span>
          )}
          <span>Wishlist</span>
        </Link>
        <Link 
          to="/cart" 
          className="mobile-nav-item"
        >
          <ShoppingBagIcon size={20} />
          {cartCount > 0 && (
            <span className="badge-count">{cartCount}</span>
          )}
          <span>Cart</span>
        </Link>
      </div>
    </div>
  );
};

export default Category;
